import React, { useState, useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import { FaCircleInfo } from "react-icons/fa6";

const Information = () => {
  const [open, setOpen] = useState(false);
  const infoRef = useRef(null);
  // Leaflet's own drag/click handling swallows clicks on overlay controls
  // unless propagation to the map is explicitly disabled on the DOM node.
  const infoPanelRef = useCallback((node) => {
    infoRef.current = node;
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  const btnRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Content for the Info component, directly defined here
  const infoContent = (
    <div>
      <h3 style={{fontSize: '1em', marginBottom: '5px'}}>Historical Rainfall</h3>
      <p style={{fontSize: '0.9em', marginTop: '0'}}>
        This data is based on historical hazard information and rainfall from the specified sources.
      </p>
      
      <div style={{marginTop: '15px'}}>
        <h4 style={{fontSize: '0.9em', marginBottom: '5px'}}>Historical Rainfall Events</h4>
        <ul style={{listStyle: 'disc', marginLeft: '20px', paddingLeft: '0'}}>
          <li>
            <strong>4 Apr 2021 Cyclone Seroja</strong>
            <p style={{margin: '5px 0 0 0', fontSize: '0.8em'}}>
              Based on 24-hour accumulated rainfall of 341.8 mm of rainfall recorded in Dili International Airport. Caused 47 deaths, 24 missing, and affected 31,926 families and 5,352 houses. All 13 municipalities were affected.
            </p>
          </li>
          <li>
            <strong>June 2022 Flooding</strong>
            <p style={{margin: '5px 0 0 0', fontSize: '0.8em'}}>
              Impacted 247 households (547 people) in Kovalima, 416 households (1,004 people) in Viqueque, and 206 households (1,014 people) in Manufahi.
            </p>
          </li>
          <li>
            <strong>8 Dec 2020 Flooding</strong>
            <p style={{margin: '5px 0 0 0', fontSize: '0.8em'}}>
              Affected 59 families in Dili.
            </p>
          </li>
          <li>
            <strong>6 Dec 2020 Flooding</strong>
            <p style={{margin: '5px 0 0 0', fontSize: '0.8em'}}>
              Affected 88 families in Covalima.
            </p>
          </li>
          <li>
            <strong>12-14 May 2020 Landslides & Flooding</strong>
            <p style={{margin: '5px 0 0 0', fontSize: '0.8em'}}>
              Affected 2,225 families in Kovalima, Lautem, Baucau, Viqueque, Manatuto & Manufahi.
            </p>
          </li>
          <li>
            <strong>13 Mar 2020 Flooding</strong>
            <p style={{margin: '5px 0 0 0', fontSize: '0.8em'}}>
              Affected 9,000 people, 7 injured, and 190 houses destroyed in Dili.
            </p>
          </li>
          <li>
            <strong>21 Jan 2020 Flooding</strong>
            <p style={{margin: '5px 0 0 0', fontSize: '0.8em'}}>
              Affected 325 families in Dili.
            </p>
          </li>
          <li>
            <strong>29 Dec 2019 Flooding</strong>
            <p style={{margin: '5px 0 0 0', fontSize: '0.8em'}}>
              Affected 225 families in Dili.
            </p>
          </li>
          <li>
            <strong>May 2019 Cyclone & Flooding</strong>
            <p style={{margin: '5px 0 0 0', fontSize: '0.8em'}}>
              Affected some homes and basic infrastructure in Baucau and Lautem.
            </p>
          </li>
          <li>
            <strong>17-18 Mar 2019 Flooding</strong>
            <p style={{margin: '5px 0 0 0', fontSize: '0.8em'}}>
              Affected 325 families in Dili.
            </p>
          </li>
          <li>
            <strong>July 2013 Floods</strong>
            <p style={{margin: '5px 0 0 0', fontSize: '0.8em'}}>
              Affected 2,572 families (approx. 20,624 people) in Covalima, Viqueque, Manufahi and Baucau.
            </p>
          </li>
        </ul>
      </div>

      <p style={{marginTop: '15px', fontSize: '0.8em'}}>
        <strong>Historical event source:</strong> IFRC Simplified Early Action Protocol<br/>
        <strong>Rainfall source:</strong> NASA GPM L3 IMERG V07
      </p>
      
      <p style={{marginTop: '15px', fontSize: '0.8em'}}>
        <strong>Reference:</strong><br/>
        World Bank. Learning from Tropical Cyclone Seroja: Building Disaster and Climate Resilience in Timor-Leste (English). Washington, D.C. : World Bank Group.<br/>
        <a href="http://documents.worldbank.org/curated/en/099245001042214184" target="_blank" rel="noopener noreferrer">http://documents.worldbank.org/curated/en/099245001042214184</a>
      </p>
    </div>
  );

  return (
    <div style={{ position: "absolute", bottom: "20px", left: "12px", zIndex: 999 }}>
      {/* Info Button */}
      <button
        ref={btnRef}
        onClick={() => setOpen(!open)}
        title="More Info"
        style={{
          background: "rgba(255, 255, 255, 0.15)",
          backdropFilter: "blur(10px) brightness(1.1)",
          WebkitBackdropFilter: "blur(10px) brightness(1.1)",
          border: "1px solid rgba(255, 255, 255, 0.25)",
          borderRadius: "8px",
          padding: "8px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: 16,
          color: "black",
          transition: "transform 0.2s",
        }}
        aria-label="Show info"
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1.0)")}
      >
        <FaCircleInfo />
      </button>

      {/* Info Card */}
      {open && (
        <div
          ref={infoPanelRef}
          style={{
            position: "absolute",
            bottom: 30,
            left: 0,
            minWidth: 260,
            maxWidth: 350,
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            padding: "16px 18px",
            fontSize: 13,
            color: "#333",
            zIndex: 1000,
            animation: "fadeIn 0.3s ease",

          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h5 style={{ fontWeight: 600, color: "#000" }}>Information</h5>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: 18,
                cursor: "pointer",
                color: "#888",
              }}
              aria-label="Close info"
            >
              ×
            </button>
          </div>
          {/* Scrollable content container */}
          <div style={{ height: '300px', overflowY: 'auto', paddingRight: '10px' }}>
            {infoContent}
          </div>
        </div>
      )}

      {/* Fade-in Animation (consider moving to a CSS file for global styles) */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Information;